import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import type { View, Period, ChartType, ProjectStatus } from './types'

interface AppState {
  activeView: View
  period: Period
  chartType: ChartType
  theme: 'dark' | 'light'
  lang: 'fr' | 'en'
  searchOpen: boolean
  compareMode: boolean
  statusFilter: ProjectStatus
}

interface AppCtx extends AppState {
  setView: (v: View) => void
  setPeriod: (p: Period) => void
  setChartType: (c: ChartType) => void
  toggleTheme: () => void
  toggleLang: () => void
  openSearch: () => void
  closeSearch: () => void
  toggleCompare: () => void
  setStatusFilter: (s: ProjectStatus) => void
}

const Ctx = createContext<AppCtx>(null as unknown as AppCtx)

function loadTheme(): 'dark' | 'light' {
  const s = localStorage.getItem('cc-theme') as 'dark' | 'light' | null
  if (s) return s
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>({
    activeView: 'overview',
    period: (localStorage.getItem('cc-period') as AppState['period'] | null) ?? 'day',
    chartType: 'line',
    theme: loadTheme(),
    lang: (localStorage.getItem('cc-lang') as 'fr' | 'en' | null) ?? 'fr',
    searchOpen: false,
    compareMode: false,
    statusFilter: 'all',
  })

  const setView = useCallback((v: View) => setState((s) => ({ ...s, activeView: v })), [])
  const setPeriod = useCallback((p: Period) => {
    localStorage.setItem('cc-period', p)
    setState((s) => ({ ...s, period: p }))
  }, [])
  const setChartType = useCallback((c: ChartType) => setState((s) => ({ ...s, chartType: c })), [])

  const toggleTheme = useCallback(() =>
    setState((s) => {
      const next = s.theme === 'dark' ? 'light' : 'dark'
      localStorage.setItem('cc-theme', next)
      document.documentElement.setAttribute('data-theme', next === 'light' ? 'light' : '')
      return { ...s, theme: next }
    }), [])

  const toggleLang = useCallback(() =>
    setState((s) => {
      const next: 'fr' | 'en' = s.lang === 'fr' ? 'en' : 'fr'
      localStorage.setItem('cc-lang', next)
      return { ...s, lang: next }
    }), [])

  const openSearch = useCallback(() => setState((s) => ({ ...s, searchOpen: true })), [])
  const closeSearch = useCallback(() => setState((s) => ({ ...s, searchOpen: false })), [])
  const toggleCompare = useCallback(() => setState((s) => ({ ...s, compareMode: !s.compareMode })), [])
  const setStatusFilter = useCallback((sf: ProjectStatus) => setState((s) => ({ ...s, statusFilter: sf })), [])

  return (
    <Ctx.Provider value={{ ...state, setView, setPeriod, setChartType, toggleTheme, toggleLang, openSearch, closeSearch, toggleCompare, setStatusFilter }}>
      {children}
    </Ctx.Provider>
  )
}

export function useApp() { return useContext(Ctx) }
